import type { ProjectJob } from '../api/types';
import { parseUtcDate, startOfTehranDay } from './datetime';

export type StatsRange = 1 | 7 | 14 | 30;

/** بازهٔ ثابت همهٔ تاریخچه‌ها (دستی / زمان‌بندی / ایندکس / بازرسی). */
export const HISTORY_WINDOW_DAYS: StatsRange = 30;
export const HISTORY_WINDOW_LABEL = '۳۰ روز اخیر';

export const STATS_RANGE_OPTIONS: { value: StatsRange; label: string }[] = [
  { value: 1, label: 'امروز' },
  { value: 7, label: '۷ روز' },
  { value: 14, label: '۱۴ روز' },
  { value: 30, label: '۳۰ روز' },
];

/** Range start in Tehran calendar days (returned as UTC instant). */
export function startOfRange(days: StatsRange, now = new Date()): Date {
  const start = startOfTehranDay(now);
  if (days > 1) {
    start.setUTCDate(start.getUTCDate() - (days - 1));
  }
  return start;
}

export function jobsInRange(jobs: ProjectJob[], days: StatsRange): ProjectJob[] {
  const from = startOfRange(days).getTime();
  return jobs.filter(
    (job) => parseUtcDate(job.createdAt).getTime() >= from,
  );
}

/** آمار بالای صفحه — همان بازه؛ برای تب دستی/زمان‌بندی همان trigger، برای نمودار همه. */
export function jobsForPageStats(
  jobs: ProjectJob[],
  days: StatsRange,
  trigger: 'manual' | 'schedule' | 'charts',
): ProjectJob[] {
  const inRange = jobsInRange(jobs, days);
  if (trigger === 'charts') return inRange;
  return inRange.filter((job) => (job.trigger ?? 'manual') === trigger);
}

export function startOfGoogleQuotaDay(now = new Date()): Date {
  const resetUtcMs = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    7,
    0,
    0,
    0,
  );
  if (now.getTime() >= resetUtcMs) return new Date(resetUtcMs);
  return new Date(resetUtcMs - 24 * 60 * 60 * 1000);
}

/**
 * سهمیه روزانهٔ Indexing API سمت گوگل است و حدود ساعت ۱۰:۳۰ به وقت تهران
 * ریست می‌شود (نیمه‌شب Pacific در PDT = ۰۷:۰۰ UTC).
 */
export const GOOGLE_QUOTA_RESET_LABEL = '۱۰:۳۰ تهران';

/** برچسب کوتاه برای سهمیهٔ بازرسی (پنجرهٔ متحرک ۲۴ ساعته). */
export const INSPECT_QUOTA_WINDOW_LABEL = 'پنجرهٔ ۲۴ ساعتهٔ متحرک';

export function jobsInGoogleQuotaDay(jobs: ProjectJob[]): ProjectJob[] {
  const from = startOfGoogleQuotaDay().getTime();
  return jobs.filter(
    (job) => parseUtcDate(job.createdAt).getTime() >= from,
  );
}

/** شروع پنجرهٔ متحرک ۲۴ ساعته برای URL Inspection. */
export function startOfInspectQuotaWindow(now = new Date()): Date {
  return new Date(now.getTime() - 24 * 60 * 60 * 1000);
}

export function jobsInInspectQuotaWindow(jobs: ProjectJob[]): ProjectJob[] {
  const from = startOfInspectQuotaWindow().getTime();
  return jobs.filter(
    (job) => parseUtcDate(job.createdAt).getTime() >= from,
  );
}

/** تعداد URLهای ارسال‌شده به Indexing API از ریست سهمیه تا الان. */
export function googleIndexingUsedToday(jobs: ProjectJob[]): number {
  return jobsInGoogleQuotaDay(jobs)
    .filter((job) => job.kind === 'google_index')
    .reduce((sum, job) => sum + job.total, 0);
}

/** تعداد URLهای بازرسی‌شده در ۲۴ ساعت گذشته (پنجرهٔ متحرک). */
export function inspectUsedToday(jobs: ProjectJob[]): number {
  return jobsInInspectQuotaWindow(jobs)
    .filter((job) => job.kind === 'inspect')
    .reduce((sum, job) => sum + job.total, 0);
}

/** تعداد URLهای ارسال‌شده به Indexing API در بازهٔ انتخاب‌شده. */
export function googleIndexingUsedInRange(
  jobs: ProjectJob[],
  days: StatsRange,
): number {
  return jobsInRange(jobs, days)
    .filter((job) => job.kind === 'google_index')
    .reduce((sum, job) => sum + job.total, 0);
}

/** تعداد URLهای بازرسی‌شده در بازهٔ انتخاب‌شده. */
export function inspectUsedInRange(
  jobs: ProjectJob[],
  days: StatsRange,
): number {
  return jobsInRange(jobs, days)
    .filter((job) => job.kind === 'inspect')
    .reduce((sum, job) => sum + job.total, 0);
}

/** برچسب کوتاه بازه برای hint کارت‌ها. */
export function statsRangeHint(days: StatsRange): string {
  const option = STATS_RANGE_OPTIONS.find((item) => item.value === days);
  return option?.label ?? `${days} روز`;
}
